import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Package, AlertCircle, CheckCircle, Trash2, Scan, Truck, Clock, Inbox, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StaffIdInput from '@/components/StaffIdInput';
import { useOperationDraft } from '@/hooks/useOperationDraft';
import { DraftsMenu } from '@/components/DraftsMenu';
import { ResumedDraftBanner } from '@/components/ResumedDraftBanner';

import { EwbDocPanel } from '@/components/ewayBill/EwbDocPanel';
const FROM_SECTION = 'Greige';

const GreigeInternalTransfer = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffIdLocked, setStaffIdLocked] = useState(false);
  const [toSection, setToSection] = useState('');
  const [isRework, setIsRework] = useState(false);
  const [reworkReason, setReworkReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [sections, setSections] = useState([]);
  const [bundleCode, setBundleCode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Outgoing: Transfers FROM Greige that need shipment confirmation (status: requested)
  const [pendingShipments, setPendingShipments] = useState([]);
  const [confirmingShipment, setConfirmingShipment] = useState(null);
  
  // Incoming: Shipped transfers TO Greige that need acceptance (status: shipped)
  const [shippedTransfers, setShippedTransfers] = useState([]);
  const [acceptingTransfer, setAcceptingTransfer] = useState(null);
  
  const [activeTab, setActiveTab] = useState('outgoing');
  const [loading, setLoading] = useState(false);
  const [resumedDraft, setResumedDraft] = useState(null);

  const bundleInputRef = useRef(null);

  const {
    drafts, activeDraftId, saving, lastSavedAt,
    autoSave, clearDraft, loadDraft, removeDraft,
  } = useOperationDraft({ moduleKey: `section:${FROM_SECTION}`, operationType: 'internal_transfer' });

  useEffect(() => {
    if (!staffIdLocked) return;
    autoSave({
      label: `${FROM_SECTION} → ${toSection || 'destination'}`,
      bundleCount: scannedBundles.length,
      documentId: `${FROM_SECTION}->${toSection || 'destination'}`,
      documentNo: toSection || '',
      payload: { staffId, staffName, toSection, isRework, reworkReason, remarks, scannedBundles },
    });
  }, [scannedBundles, staffIdLocked, toSection, isRework, reworkReason, remarks, staffId, staffName, autoSave]);

  const handleResumeDraft = async (draftId) => {
    try {
      const doc = await loadDraft(draftId);
      const p = doc.payload || {};
      setStaffId(p.staffId || '');
      setStaffName(p.staffName || '');
      setStaffIdLocked(!!p.staffId);
      setToSection(p.toSection || '');
      setIsRework(!!p.isRework);
      setReworkReason(p.reworkReason || '');
      setRemarks(p.remarks || '');
      setScannedBundles(p.scannedBundles || []);
      setActiveTab('outgoing');
      const summary = (drafts || []).find((x) => x.draft_id === draftId) || null;
      setResumedDraft(summary);
      toast.success(summary?.last_updated_by_name ? `Resumed draft — last worked on by ${summary.last_updated_by_name}` : 'Draft restored');
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } catch {
      toast.error('Failed to restore draft');
    }
  };

  const handlePickDestination = (value) => {
    setToSection(value);
    if (scannedBundles.length > 0) return;
    const existing = (drafts || []).find((d) => d.document_id === `${FROM_SECTION}->${value}`);
    if (existing) handleResumeDraft(existing.draft_id);
    else setResumedDraft(null);
  };

  const handleStartFresh = async () => {
    const id = resumedDraft?.draft_id;
    if (id) {
      const ok = await removeDraft(id);
      if (!ok) { toast.error('Could not discard the shared draft — please try again'); return; }
    }
    setResumedDraft(null);
    setScannedBundles([]);
    setToSection('');
    setRemarks('');
    setIsRework(false);
    setReworkReason('');
  };

  useEffect(() => {
    fetchSections();
    fetchAllData();
  }, []);

  useEffect(() => {
    if (staffIdLocked && bundleInputRef.current) {
      bundleInputRef.current.focus();
    }
  }, [staffIdLocked]);

  const fetchSections = async () => {
    try {
      const response = await apiClient.get('/internal-transfer/sections', {
        params: { exclude_section: FROM_SECTION }
      });
      setSections(response.data.sections || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
      toast.error('Failed to load sections');
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // 1. Get shipment requests FROM Greige (status: requested) - need to confirm shipment
      const shipmentRes = await apiClient.get('/internal-transfer/shipment-requests', {
        params: { from_section: FROM_SECTION }
      });
      setPendingShipments(shipmentRes.data.transfers || []);
      
      // 2. Get shipped transfers TO Greige (status: shipped) - need to accept
      const shippedRes = await apiClient.get('/internal-transfer/shipped-transfers', {
        params: { section: FROM_SECTION }
      });
      setShippedTransfers(shippedRes.data.transfers || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Step 6: Accept shipped transfer (destination receives goods)
  const handleAcceptTransfer = async (transferId) => {
    setAcceptingTransfer(transferId);
    try {
      await apiClient.post('/internal-transfer/accept', {
        transfer_id: transferId
      });
      toast.success('Transfer received! Proceed to Bundle Receipt Entry.');
      fetchAllData();
    } catch (error) {
      console.error('Error accepting transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept transfer');
    } finally {
      setAcceptingTransfer(null);
    }
  };

  // Step 4: Confirm shipment (source ships goods)
  const handleConfirmShipment = async (transferId) => {
    setConfirmingShipment(transferId);
    try {
      await apiClient.post('/internal-transfer/confirm-shipment', {
        transfer_id: transferId
      });
      toast.success('Shipment confirmed successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error confirming shipment:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm shipment');
    } finally {
      setConfirmingShipment(null);
    }
  };

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffIdLocked(true);
    }
  };

  const handleScanBundle = async (e) => {
    e.preventDefault();
    
    if (!toSection) {
      toast.error('Please select destination section first');
      return;
    }

    if (!bundleCode.trim()) {
      toast.error('Please scan bundle code');
      bundleInputRef.current?.focus();
      return;
    }

    const bundle = bundleCode.trim().toUpperCase();

    if (scannedBundles.includes(bundle)) {
      toast.error('Bundle already scanned');
      setBundleCode('');
      bundleInputRef.current?.focus();
      return;
    }

    setIsProcessing(true);
    try {
      const response = await apiClient.post('/internal-transfer/validate-bundle', null, {
        params: { bundle_code: bundle, from_section: FROM_SECTION, to_section: toSection }
      });

      if (response.data.valid) {
        setScannedBundles([...scannedBundles, bundle]);
        toast.success(`✓ Bundle ${bundle} added`);
        setBundleCode('');
      }
    } catch (error) {
      console.error('Bundle validation error:', error);
      toast.error(error.response?.data?.detail || 'Invalid bundle code');
      setBundleCode('');
    } finally {
      setIsProcessing(false);
      setTimeout(() => bundleInputRef.current?.focus(), 50);
    }
  };

  const handleRemoveBundle = (bundle) => {
    setScannedBundles(scannedBundles.filter(b => b !== bundle));
    toast.info(`Bundle ${bundle} removed`);
  };

  const handleSubmit = async () => {
    if (scannedBundles.length === 0) {
      toast.error('No bundles scanned');
      return;
    }

    setIsSubmitting(true);
    try {
      const isReworkSubmit = isRework && (toSection || '').toLowerCase().includes('inhouse');
      if (isReworkSubmit && !reworkReason.trim()) {
        toast.error('Provide a rework reason');
        setIsSubmitting(false);
        return;
      }
      const response = await apiClient.post('/internal-transfer/create', {
        staff_id: staffId,
        to_section: toSection,
        bundle_codes: scannedBundles,
        from_section: FROM_SECTION,
        transfer_type: isReworkSubmit ? 'REWORK' : 'STANDARD',
        rework_reason: isReworkSubmit ? reworkReason.trim() : undefined,
        remarks: remarks.trim() || undefined,
      });

      toast.success(response.data.message);
      setScannedBundles([]);
      setToSection('');
      setBundleCode('');
      setIsRework(false);
      setReworkReason('');
      setRemarks('');
      clearDraft();
      setResumedDraft(null);
      
      setTimeout(() => {
        toast.success(`Transfer ID: ${response.data.transfer_id}`);
      }, 500);
      
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create transfer request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnlockStaff = () => {
    setStaffIdLocked(false);
    setStaffId('');
    setStaffName('');
    setScannedBundles([]);
    setToSection('');
    setBundleCode('');
    setResumedDraft(null);
  };

  // Count for badges
  const outgoingCount = pendingShipments.length;
  const incomingCount = shippedTransfers.length;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/sections/greige')} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Internal Transfer</h1>
              <p className="text-sm text-[#9CA3AF]">Send & receive bundles between sections</p>
            </div>
          </div>
          <DraftsMenu
            drafts={drafts}
            onResume={handleResumeDraft}
            onDelete={removeDraft}
            activeDraftId={activeDraftId}
            saving={saving}
            lastSavedAt={lastSavedAt}
            variant="dark"
          />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="outgoing" className="flex items-center gap-2" data-testid="outgoing-tab">
              <Send className="w-4 h-4" />
              Outgoing Transfers
              {outgoingCount > 0 && (
                <Badge className="bg-orange-500 text-white ml-1">{outgoingCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="incoming" className="flex items-center gap-2" data-testid="incoming-tab">
              <Truck className="w-4 h-4" />
              Incoming Shipments
              {incomingCount > 0 && (
                <Badge className="bg-blue-500 text-white ml-1">{incomingCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* OUTGOING TRANSFERS TAB */}
          <TabsContent value="outgoing">
            {resumedDraft && (
              <ResumedDraftBanner draft={resumedDraft} onStartFresh={handleStartFresh} className="mb-4" />
            )}
            {/* Pending Shipments Section */}
            {pendingShipments.length > 0 && (
              <Card className="mb-6 border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <Clock className="w-5 h-5" />
                    Pending Shipments ({pendingShipments.length})
                  </CardTitle>
                  <CardDescription>Transfers accepted by receiving sections - confirm to ship</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingShipments.map((shipment) => (
                      <div 
                        key={shipment.transfer_id} 
                        className="flex items-center justify-between p-4 bg-white rounded-lg border border-orange-200"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                              {shipment.transfer_id}
                            </Badge>
                            <span className="text-sm text-gray-600">→</span>
                            <Badge className="bg-purple-100 text-purple-700 border-0">
                              {shipment.to_section}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {shipment.bundle_count || shipment.bundle_codes?.length || 0} bundles • Accepted by {shipment.accepted_by_name || 'Section'}
                          </p>
                          <p className="text-xs text-gray-400">
                            Requested: {new Date(shipment.requested_at).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          onClick={() => handleConfirmShipment(shipment.transfer_id)}
                          disabled={confirmingShipment === shipment.transfer_id}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`confirm-shipment-${shipment.transfer_id}`}
                        >
                          {confirmingShipment === shipment.transfer_id ? (
                            <>Confirming...</>
                          ) : (
                            <>
                              <Truck className="w-4 h-4 mr-2" />
                              Confirm Shipment
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Panel - Input Form */}
              <div className="space-y-6">
                {/* Step 1: Staff ID */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                      Staff Identification
                    </CardTitle>
                    <CardDescription>Scan staff ID for accountability</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {!staffIdLocked ? (
                      <StaffIdInput onValidated={handleStaffValidated} />
                    ) : (
                      <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div>
                          <p className="text-sm text-green-700">Staff ID: <strong>{staffId}</strong></p>
                          <p className="text-sm text-green-600">{staffName}</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleUnlockStaff}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          Change
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Step 2: Select Destination Section */}
                {staffIdLocked && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <span className="bg-purple-100 text-purple-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                        Destination Section
                      </CardTitle>
                      <CardDescription>Select section to transfer bundles to</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Label className="text-sm text-[#5A5F6E]">To Section *</Label>
                      <Select value={toSection} onValueChange={handlePickDestination}>
                        <SelectTrigger className="mt-2" data-testid="to-section-select">
                          <SelectValue placeholder="Select destination section" />
                        </SelectTrigger>
                        <SelectContent>
                          {sections.map((section) => (
                            <SelectItem key={section.section_name} value={section.section_name}>
                              {section.section_name}
                              {section.description && (
                                <span className="text-xs text-gray-500 ml-2">- {section.description}</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {(toSection || '').toLowerCase().includes('inhouse') && (
                        <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm">
                          <label className="flex items-center gap-2 font-medium text-amber-900 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isRework}
                              onChange={(e) => setIsRework(e.target.checked)}
                              data-testid="rework-toggle"
                              className="h-4 w-4 accent-amber-600"
                            />
                            Send for Rework / Re-Assortment
                          </label>
                          {isRework && (
                            <>
                              <div className="mt-2 text-xs text-amber-800">
                                On receipt at Inhouse, these bundles will be <b>fully retired</b>
                                and their USNs released into the Rework Pool.
                              </div>
                              <Input
                                value={reworkReason}
                                onChange={(e) => setReworkReason(e.target.value)}
                                placeholder="Reason for rework"
                                className="mt-2 bg-white"
                                data-testid="rework-reason-input"
                              />
                            </>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Step 3: Scan Bundles */}
                {staffIdLocked && toSection && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <span className="bg-orange-100 text-orange-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                        Scan Bundles
                      </CardTitle>
                      <CardDescription>Scan bundle codes to transfer</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleScanBundle}>
                        <div className="flex gap-2">
                          <Input
                            ref={bundleInputRef}
                            value={bundleCode}
                            onChange={(e) => setBundleCode(e.target.value)}
                            placeholder="Scan Bundle Code (BUN000001/25-26)"
                            className="flex-1"
                            disabled={isProcessing}
                            autoFocus
                            data-testid="bundle-code-input"
                          />
                          <Button 
                            type="submit" 
                            disabled={isProcessing || !bundleCode.trim()}
                            className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                          >
                            <Scan className="w-4 h-4" />
                          </Button>
                        </div>
                      </form>

                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-800">
                          <AlertCircle className="w-3 h-3 inline mr-1" />
                          <strong>Note:</strong> Only bundles that have completed Packing (Operation 7) <em>and</em> Reconcile can be transferred.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Remarks (Optional) */}
                {scannedBundles.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Remarks (Optional)</CardTitle>
                      <CardDescription>Note for the receiving section</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="e.g. handle this lot first, urgent for shipment…"
                        rows={2}
                        data-testid="remarks-input"
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Submit Button */}
                {scannedBundles.length > 0 && (
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full bg-green-600 hover:bg-green-700 h-12 text-base"
                    data-testid="submit-transfer-btn"
                  >
                    {isSubmitting ? (
                      <>Processing...</>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit Transfer Request ({scannedBundles.length} bundles)
                      </>
                    )}
                  </Button>
                )}
              </div>

              {/* Right Panel - Scanned Bundles List */}
              <div>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Package className="w-5 h-5" />
                        Scanned Bundles
                      </span>
                      <span className="text-sm font-normal text-gray-500">
                        {scannedBundles.length} bundle{scannedBundles.length !== 1 ? 's' : ''}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {scannedBundles.length === 0 ? (
                      <div className="text-center py-12 text-gray-400">
                        <Package className="w-16 h-16 mx-auto mb-3 opacity-20" />
                        <p>No bundles scanned yet</p>
                        <p className="text-sm mt-1">Scan bundles to add them to transfer</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[600px] overflow-y-auto">
                        {scannedBundles.map((bundle, index) => (
                          <div
                            key={bundle}
                            className="flex items-center justify-between p-3 bg-[#F5F1E8] border border-[#E5DFD0] rounded-lg hover:bg-[#EDE9E0] transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-gray-500 w-8">
                                #{index + 1}
                              </span>
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="font-mono font-semibold">{bundle}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveBundle(bundle)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Summary Card */}
                {scannedBundles.length > 0 && toSection && (
                  <Card className="mt-4 border-green-200 bg-green-50">
                    <CardContent className="pt-6">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700">From:</span>
                          <span className="font-semibold">{FROM_SECTION} Section</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">To:</span>
                          <span className="font-semibold text-purple-700">{toSection}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Staff:</span>
                          <span className="font-semibold">{staffId} - {staffName}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-green-200">
                          <span className="text-gray-700 font-semibold">Total Bundles:</span>
                          <span className="font-bold text-green-700 text-lg">{scannedBundles.length}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* INCOMING SHIPPED TRANSFERS TAB - Step 5 & 6 */}
          <TabsContent value="incoming">
            {shippedTransfers.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center text-gray-400">
                    <Truck className="w-16 h-16 mx-auto mb-3 opacity-20" />
                    <p className="text-lg font-medium">No incoming shipments</p>
                    <p className="text-sm mt-1">Shipped transfers from other sections will appear here</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <Truck className="w-5 h-5" />
                    Incoming Shipments ({shippedTransfers.length})
                  </CardTitle>
                  <CardDescription>Shipped transfers waiting for you to receive and accept</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {shippedTransfers.map((transfer) => (
                      <div 
                        key={transfer.transfer_id} 
                        className="p-4 bg-blue-50 rounded-lg border border-blue-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                                {transfer.transfer_id}
                              </Badge>
                              <span className="text-sm text-gray-600">from</span>
                              <Badge className="bg-indigo-100 text-indigo-700 border-0">
                                {transfer.from_section}
                              </Badge>
                              <Badge className="bg-green-100 text-green-700 border-0">
                                <Truck className="w-3 h-3 mr-1" />
                                Shipped
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-700">
                              <strong>{transfer.bundle_count || transfer.bundle_codes?.length || 0}</strong> bundles
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Shipped by {transfer.shipped_by_name || 'Unknown'} on {transfer.shipped_at ? new Date(transfer.shipped_at).toLocaleString() : 'N/A'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleAcceptTransfer(transfer.transfer_id)}
                              disabled={acceptingTransfer === transfer.transfer_id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`accept-transfer-${transfer.transfer_id}`}
                            >
                              {acceptingTransfer === transfer.transfer_id ? (
                                <>Accepting...</>
                              ) : (
                                <>
                                  <Check className="w-4 h-4 mr-2" />
                                  Accept & Receive
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Bundle list preview */}
                        {transfer.bundle_codes && transfer.bundle_codes.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <p className="text-xs text-gray-500 mb-2">Bundle Codes:</p>
                            <div className="flex flex-wrap gap-1">
                              {transfer.bundle_codes.slice(0, 5).map((code) => (
                                <Badge key={code} variant="outline" className="text-xs bg-white">
                                  {code}
                                </Badge>
                              ))}
                              {transfer.bundle_codes.length > 5 && (
                                <Badge variant="outline" className="text-xs bg-gray-100">
                                  +{transfer.bundle_codes.length - 5} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        <div className="mt-6"><EwbDocPanel docType="branch_transfer" section={FROM_SECTION} title="Internal transfer e-way bills (goods leaving the site)" /></div>
      </main>
    </div>
  );
};

export default GreigeInternalTransfer;
